import { useEffect, useRef, useState } from 'react'

import { Box, TextField, Typography } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'

import api from 'common/api'

export type HighSchoolOption = {
  Id: string
  Name: string
  City: string
  State: string
  TimeZone?: string
}

type HighSchoolSearchResult = {
  value: string
  schoolName: string
  city: string
  state: string
  timeZone?: string
}

type HighSchoolTypeaheadProps = {
  onSelect: (highSchool: HighSchoolOption) => void
}

const HighSchoolTypeahead = ({ onSelect }: HighSchoolTypeaheadProps) => {
  const [options, setOptions] = useState<HighSchoolOption[]>([])
  const [value, setValue] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  let searchTimeout: ReturnType<typeof setTimeout> | undefined = undefined
  const mounted = useRef(true)

  useEffect(() => {
    return () => {
      mounted.current = false
    }
  }, [])

  const onSearch = (searchName: string) => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      api.get<HighSchoolSearchResult[]>(`/api/high_schools/find/${searchName}`).then((highSchools) => {
        if (!mounted.current) {
          return
        }

        const newOptions = highSchools.map((hs) => ({
          Id: hs.value,
          Name: hs.schoolName,
          City: hs.city,
          State: hs.state,
          TimeZone: hs.timeZone,
        }))

        if (newOptions.length > 0) {
          setOptions(newOptions)
          setOpen(true)
        }
      })
    }, 300)
  }
  return (
    <Autocomplete
      value={value}
      inputValue={inputValue}
      id="calander-hs-typeahead"
      options={options}
      aria-label="Type a high school name, town, or zip"
      onChange={(_, selection, reason) => {
        if (reason === 'select-option' && selection && typeof selection !== 'string') {
          onSelect(selection)
        }
      }}
      noOptionsText="No matches found."
      onInputChange={(_, val, reason) => {
        setInputValue(val)

        // Only trigger onSearch when the input changes
        // Needed for new timeout - otherwise will reappear when user selects an item
        if (reason !== 'input') {
          return
        }

        if (val.length === 0) {
          setOptions([])
        } else {
          onSearch(val)
        }
      }}
      freeSolo
      clearOnBlur
      blurOnSelect
      getOptionLabel={(option) => option.Name || ''}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Enter high school name"
          variant="outlined"
        />
      )}
      renderOption={(item) => {
        return (
          <Box>
            <Typography>
              <strong>{item.Name}</strong>
            </Typography>
            <Typography>{`${item.City}, ${item.State}`}</Typography>
          </Box>
        )
      }}
      closeIcon={null}
      open={open}
      onOpen={() => {
        if (inputValue.length > 0 && options.length > 0) {
          setOpen(true)
        }
      }}
      onClose={() => {
        setOpen(false)
        setInputValue('')
        setValue('')
        setOptions([])
      }}
    />
  )
}

export default HighSchoolTypeahead
